import React from 'react';
import { useIntl } from "react-intl";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import typoRepository from "@/repository/typoRepository";
import { usePaginatedTypos } from "@/query/typo";
import { useAgentId, useModal, usePageParam } from "@/hook";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import Button from "@/component/Button";
import { SearchBar } from "@/component/input";
import { UploadModal } from "@/component/modal";
import PaginationBar from "@/component/PaginationBar";
import { SingleTestPanel, SingleTestButton } from '@/component/singleTest';
import { DownloadIcon, UploadIcon } from "@/assets/svgs/icons";

import TypoForm from "./component/TypoForm";
import TypoTable from "./component/TypoTable";


const TYPO_PAGE_SIZE = 15;
const TYPO_UPLOAD_MODAL_ID = "typo-upload-modal";

export interface TypoPageParam {
    page: number;
    size: number;
    searchKeyword?: string;
    sortOrder: "asc" | "desc";
}

const TypoView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();

    const { param, set: setParam } = usePageParam<TypoPageParam>({
        page: 0,
        size: TYPO_PAGE_SIZE,
        sortOrder: "asc",
    });

    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: typoPage } = usePaginatedTypos(
        agentId,
        param?.page,
        param?.size,
        param?.sortOrder,
        param?.searchKeyword,
    );

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        return await typoRepository.import(agentId, file, isCleanImport);
    };

    return (<>
        <UploadModal id={TYPO_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i07012" })}
                     cleanImportText={intl.formatMessage({ id: "i07019" })}
                     sampleFileName="nlu_typo_sample"
                     sampleFileColumns={[
                         { attribute: "correction_keyword", description: intl.formatMessage({ id: "i07017" }) },
                         { attribute: "typo", description: intl.formatMessage({ id: "i07018" }) },
                     ]}
                     upload={onClickUpload}
        />

            <div className={classNames(
                     "agent-content-layout",
                     singleTestPanelOpen && "test-panel-open",
                 )}
            >
                <div className={classNames(
                     "full-page-container flex flex-col gap-3",
                     singleTestPanelOpen && "test-panel-open",
                 )}
                >
                <div className="search-bar-container">
                    <SearchBar lg width={680}
                               placeholder={intl.formatMessage({ id: "i07008" })}
                               onSearch={query => setParam({ searchKeyword: query })}
                    />
                    <div className="search-bar-container-btn-group">
                        <Button variant="text"
                                onClick={() => modal.open(TYPO_UPLOAD_MODAL_ID)}
                        >
                            <UploadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0004" })}
                        </Button>

                        <Button variant="text"
                                onClick={() => typoRepository.export(agentId)}
                        >
                            <DownloadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0005" })}
                        </Button>
                        <SingleTestButton />
                    </div>
                </div>

                <div className="form-table-container">
                    <TypoForm />

                    <div className="table-pagination-container">
                        <TypoTable page={typoPage}
                                   pageParam={param}
                                   onClickSortHead={order => setParam({ sortOrder: order })}
                        />

                        <PaginationBar pageData={typoPage}
                                       onClickPage={page => setParam({ page: page })}
                        />
                    </div>

                    <SingleTestPanel className="ml-auto" />
                </div>
            </div>
        </div>
    </>);
};

export default TypoView;
